import {
  Bot,
  CheckCircle,
  ChevronRight,
  Cog,
  FolderCog,
  FolderOpen,
  Handshake,
  LinkIcon,
  Loader2,
  MessageSquare,
  Plug,
  Sparkles,
  User,
  Wrench,
  XCircle,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { formatDateTime, formatFullDateTime } from '../utilities';
import { getApprovalMethodLabel } from './approval-labels';
import { CompressionCard } from './CompressionCard';
import { MarkdownContent } from './MarkdownContent';
import type { ExtractedPrompt } from './SystemPromptSection';
import type {
  ExternalAgentMessageInfo,
  SubAgentCompressionInfo,
  SubAgentMessageInfo,
  ToolCategory,
} from './task-detail-types';
import {
  getCategoryBadgeColor,
  getCategoryLabel,
  groupToolMessages,
  type ToolMessageGroup,
} from './task-detail-utils';

/** 結果の最大表示文字数 */
const MAX_RESULT_LENGTH = 500;

/**
 * ツール名のプレフィックスからカテゴリを判定
 */
function getToolCategory(toolName: string): ToolCategory {
  if (toolName.startsWith('builtin__')) return 'builtin';
  // セットアップ管理ツール（ホスト定義）
  if (toolName.startsWith('manage__')) return 'manage';
  if (toolName.startsWith('sandbox__')) return 'sandbox';
  if (toolName.startsWith('subagent__')) return 'subagent';
  if (toolName.startsWith('mcp__')) return 'mcp';
  if (toolName.startsWith('external__')) return 'external';
  if (toolName.startsWith('skill__')) return 'skill';
  // ワークスペース間の協調ツール（依頼先の一覧・作業の依頼）
  if (toolName.startsWith('peer__')) return 'collaboration';
  return 'skill';
}

/**
 * カテゴリアイコンを取得
 */
function getCategoryIcon(category: ToolCategory) {
  switch (category) {
    case 'builtin':
      return <Cog className="w-3 h-3" />;
    case 'manage':
      return <FolderCog className="w-3 h-3" />;
    case 'sandbox':
      return <FolderOpen className="w-3 h-3" />;
    case 'skill':
      return <Sparkles className="w-3 h-3" />;
    case 'subagent':
      return <Bot className="w-3 h-3" />;
    case 'mcp':
      return <Plug className="w-3 h-3" />;
    case 'external':
      return <LinkIcon className="w-3 h-3" />;
    case 'collaboration':
      return <Handshake className="w-3 h-3" />;
    default:
      return <Wrench className="w-3 h-3" />;
  }
}

/**
 * サブエージェント内ツール表示コンポーネント
 */
export function SubAgentToolItem({ group }: { group: ToolMessageGroup }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // ヘッダー表示テキスト: intent があれば intent、なければ toolName
  const headerText = group.intent || group.toolName;
  const category = getToolCategory(group.toolName);

  // 承認方法バッジ（実決定スタンプ。文言・ピル表示は ToolCallCard と統一）
  const approvalMethodLabel = group.approvalOutcome
    ? getApprovalMethodLabel(group.approvalOutcome.source, group.approvalOutcome.decision)
    : undefined;

  // ステータスアイコン
  const statusIcon = group.isRunning ? (
    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
  ) : group.isError ? (
    <XCircle className="w-4 h-4 text-red-500" />
  ) : (
    <CheckCircle className="w-4 h-4 text-green-500" />
  );

  // 結果の表示（省略対応）
  const needsTruncation = (group.result?.length ?? 0) > MAX_RESULT_LENGTH;
  const displayResult =
    needsTruncation && !isExpanded
      ? `${group.result?.slice(0, MAX_RESULT_LENGTH)}...`
      : group.result;

  return (
    <div className="rounded-md border border-border bg-card text-xs">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full cursor-pointer items-center gap-2 p-2 text-left hover:bg-muted/50"
      >
        <ChevronRight
          className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <span
          className={`group/badge inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium transition-all duration-200 ${getCategoryBadgeColor(category)}`}
        >
          {getCategoryIcon(category)}
          <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover/badge:max-w-24 group-hover/badge:opacity-100">
            {getCategoryLabel(category)}
          </span>
        </span>
        <span
          className={`flex-1 font-medium text-foreground ${group.isRunning ? 'animate-pulse' : ''}`}
        >
          {headerText}
        </span>
        {/* 承認バッジはステータスアイコンの左隣（右寄せ）。デスクトップと統一 */}
        <span className="ml-auto inline-flex items-center gap-2">
          {approvalMethodLabel && (
            <span
              data-testid="subagent-tool-approval-method"
              className="rounded-full border border-border px-1.5 py-0.5 text-xs text-muted-foreground"
            >
              {approvalMethodLabel}
            </span>
          )}
          {statusIcon}
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-border p-2 space-y-2">
          {/* ツール名表示 */}
          <div className="flex items-center gap-1 text-muted-foreground">
            <span>ツール: </span>
            <code className="rounded bg-muted px-1 py-0.5 font-mono font-semibold text-foreground">
              {group.toolName}
            </code>
          </div>

          {/* 入力パラメータ */}
          {group.args && Object.keys(group.args).length > 0 && (
            <div>
              <div className="mb-1 text-muted-foreground">入力:</div>
              <pre className="overflow-x-auto rounded bg-muted/50 p-2 text-xs whitespace-pre-wrap break-words text-foreground">
                {JSON.stringify(group.args, null, 2)}
              </pre>
            </div>
          )}

          {/* 結果 */}
          {group.result && (
            <div>
              <div className="mb-1 text-muted-foreground">
                {group.isError ? 'エラー:' : '結果:'}
              </div>
              <pre
                className={`overflow-x-auto rounded p-2 text-xs whitespace-pre-wrap break-words ${
                  group.isError
                    ? 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-200'
                    : 'bg-muted/50 text-foreground'
                }`}
              >
                {displayResult}
              </pre>
              {needsTruncation && (
                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  {isExpanded ? '折りたたむ' : '全文表示'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * サブエージェントメッセージ表示コンポーネント（desktop-appと同じ構造）
 */
export function SubAgentMessagesCard({
  messages,
  agentName,
  systemPrompt,
  statusLabel = 'タスク委譲完了',
}: {
  messages: (SubAgentMessageInfo | SubAgentCompressionInfo)[];
  agentName?: string | undefined;
  systemPrompt?: ExtractedPrompt | undefined;
  /** 見出しの文言（委譲以外の作業者、例えば完了チェッカーで使う） */
  statusLabel?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // メッセージのみ抽出（compression を除外）
  const messageEntries = messages.filter(
    (entry): entry is SubAgentMessageInfo => !('type' in entry && entry.type === 'compression'),
  );

  // ステップ数をカウント（内部ループカウンタと一致する stepNumber の最大値）
  const stepCount = messageEntries.reduce(
    (max, m) => (m.step !== undefined && m.step > max ? m.step : max),
    0,
  );

  // ツールメッセージをグルーピング
  const toolGroups = groupToolMessages(messageEntries);

  // ツールメッセージのインデックスを追跡（各グループの最初のメッセージ位置）
  const groupByTimestamp = new Map<string, ToolMessageGroup>();
  for (const group of toolGroups) {
    if (group.callTimestamp) {
      groupByTimestamp.set(group.callTimestamp, group);
    }
  }

  // レンダリング用のエントリーを作成
  const renderEntries = (() => {
    const result: React.ReactNode[] = [];
    const renderedGroupIds = new Set<string>();

    messages.forEach((entry, index) => {
      // compression エントリは CompressionCard で表示
      if ('type' in entry && entry.type === 'compression') {
        const compression = entry as SubAgentCompressionInfo;
        result.push(
          <CompressionCard
            key={`compression-${compression.timestamp ?? index}`}
            part={{
              type: 'compression',
              summaryText: compression.summaryText,
              stats: compression.stats,
              timestamp: compression.timestamp,
            }}
          />,
        );
        return;
      }

      const message = entry as SubAgentMessageInfo;

      if (message.role === 'tool') {
        // ツールメッセージに対応するグループを探す
        let matchedGroup: ToolMessageGroup | undefined;

        // 1. toolCallId でマッチ
        if (message.toolCallId) {
          matchedGroup = toolGroups.find((g) => g.toolCallId === message.toolCallId);
        }

        // 2. timestamp と toolName でマッチ（toolCallId がない場合）
        if (!matchedGroup && message.timestamp) {
          matchedGroup = groupByTimestamp.get(message.timestamp);
        }

        if (matchedGroup && !renderedGroupIds.has(matchedGroup.toolCallId)) {
          renderedGroupIds.add(matchedGroup.toolCallId);
          result.push(
            <SubAgentToolItem key={`tool-group-${matchedGroup.toolCallId}`} group={matchedGroup} />,
          );
        }
        // マッチしない場合はスキップ（グループで表示される）
      } else {
        // user/assistant メッセージは SubAgentMessageItem で表示
        result.push(
          <SubAgentMessageItem
            key={`message-${message.timestamp ?? index}-${index}`}
            message={message}
          />,
        );
      }
    });

    return result;
  })();

  return (
    <div className="mb-4 rounded-md border border-blue-300 bg-blue-50 dark:border-blue-400/40 dark:bg-blue-950/20">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 rounded-t-md bg-blue-100 p-2 text-muted-foreground text-sm cursor-pointer hover:bg-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <MessageSquare className="w-4 h-4 text-blue-500" />
        <span>
          {statusLabel}
          {agentName && (
            <span className="ml-1 text-blue-600 dark:text-blue-400">→ {agentName}</span>
          )}
          <span className="ml-1 text-blue-500">{stepCount} ステップ</span>
        </span>
      </button>
      {isOpen && (
        <div className="max-h-96 space-y-2 overflow-y-auto border-t border-blue-200 dark:border-blue-400/30 p-2">
          {/* サブエージェントのシステムプロンプト */}
          {systemPrompt && (
            <div className="rounded border border-orange-500/30 bg-orange-500/5">
              <button
                type="button"
                onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                className="flex w-full items-center gap-2 p-2 text-left text-xs hover:bg-orange-500/10 transition-colors"
              >
                <ChevronRight
                  className={`w-3 h-3 shrink-0 text-orange-600 dark:text-orange-400 transition-transform ${showSystemPrompt ? 'rotate-90' : ''}`}
                />
                <Cog className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                <span className="text-orange-600 dark:text-orange-400">システムプロンプト</span>
              </button>
              {showSystemPrompt && (
                <div className="px-2 pb-2">
                  <div className="prose dark:prose-invert prose-xs max-w-none overflow-x-auto text-foreground bg-muted/50 rounded p-2">
                    <MarkdownContent content={systemPrompt.text} />
                  </div>
                </div>
              )}
            </div>
          )}
          {renderEntries}
        </div>
      )}
    </div>
  );
}

/**
 * サブエージェントメッセージアイテム（user/assistantメッセージ用）
 */
function SubAgentMessageItem({ message }: { message: SubAgentMessageInfo }) {
  // user メッセージはヘッダー付きで表示
  if (message.role === 'user') {
    return (
      <div className="rounded p-2 text-xs bg-blue-500/10">
        <div className="mb-1 flex items-center gap-1 text-muted-foreground">
          <User className="w-3 h-3" />
          <span className="font-medium">Task</span>
          {message.timestamp && (
            <span className="text-[10px]" title={formatFullDateTime(message.timestamp)}>
              {formatDateTime(message.timestamp)}
            </span>
          )}
        </div>
        <div className="prose dark:prose-invert prose-xs max-w-none overflow-x-auto text-foreground">
          <MarkdownContent content={message.content} />
        </div>
      </div>
    );
  }

  // assistant メッセージはシンプルにコンテンツのみ表示
  return (
    <div className="prose dark:prose-invert prose-xs max-w-none overflow-x-auto text-xs text-foreground">
      <MarkdownContent content={message.content} />
    </div>
  );
}

/**
 * 外部エージェントメッセージ表示コンポーネント（desktop-appと同じ構造）
 */
export function ExternalAgentMessagesCard({
  messages,
  agentName,
  agentType,
}: {
  messages: ExternalAgentMessageInfo[];
  agentName: string | undefined;
  agentType: 'a2a' | 'dify' | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const typeLabel = agentType === 'a2a' ? 'A2A' : agentType === 'dify' ? 'Dify' : '外部';

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-muted-foreground text-sm cursor-pointer hover:text-foreground transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <LinkIcon className="w-4 h-4 text-pink-500" />
        <span>
          {typeLabel} 連携完了
          {agentName && <span className="ml-1">→ {agentName}</span>}
        </span>
      </button>
      {isOpen && (
        <div className="mt-4 max-h-96 space-y-2 overflow-y-auto">
          {messages.map((msg, index) => (
            <ExternalAgentMessageItem key={`${msg.timestamp ?? index}-${index}`} message={msg} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 外部エージェントメッセージアイテム
 */
function ExternalAgentMessageItem({ message }: { message: ExternalAgentMessageInfo }) {
  const roleLabel = message.role === 'user' ? 'Request' : 'Response';

  const roleIcon =
    message.role === 'user' ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />;

  return (
    <div
      className={`rounded p-2 text-xs ${
        message.role === 'user' ? 'bg-pink-500/10' : 'bg-muted/50'
      }`}
    >
      <div className="mb-1 flex items-center gap-1 text-muted-foreground">
        {roleIcon}
        <span className="font-medium">{roleLabel}</span>
        {message.timestamp && (
          <span className="text-[10px]" title={formatFullDateTime(message.timestamp)}>
            {formatDateTime(message.timestamp)}
          </span>
        )}
      </div>
      <div className="prose dark:prose-invert prose-xs max-w-none overflow-x-auto text-foreground">
        <MarkdownContent content={message.content} />
      </div>
    </div>
  );
}
